import { mergeAttributes, Node } from '@tiptap/core'
import { ButtonAttributes } from '../interfaces/ButtonAttributes'
import { generateButtonStyles } from '../utils/generateButtonStyles'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { EditorButtonComponent } from '../components/EditorButtonComponent'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    button: {
      /**
       * Add a button
       */
      setButton: (options?: Omit<Partial<ButtonAttributes>, 'HTMLAttributes'>) => ReturnType

      /**
       * Edit button attributes
       */
      setButtonAttributes: (
        options: Omit<Partial<ButtonAttributes>, 'HTMLAttributes'>,
      ) => ReturnType
    }
  }
}

export const Button = Node.create({
  name: 'button',
  group: 'block',
  atom: true,
  draggable: true,
  priority: 1000,

  addAttributes() {
    return {
      emailComponent: {
        default: 'button',
      },
      text: {
        default: 'Get Started',
        parseHTML: (element) => element.getAttribute('data-text'),
        renderHTML: (attributes) => ({
          'data-text': attributes.text,
        }),
      },
      url: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-url'),
        renderHTML: (attributes) => ({
          'data-url': attributes.url,
        }),
      },
      alignment: {
        default: 'center',
        parseHTML: (element) => element.getAttribute('data-alignment'),
        renderHTML: (attributes) => ({
          'data-alignment': attributes.alignment,
        }),
      },
      variant: {
        default: 'filled',
        parseHTML: (element) => element.getAttribute('data-variant'),
        renderHTML: (attributes) => ({
          'data-variant': attributes.variant,
        }),
      },
      borderRadius: {
        default: 'round',
        parseHTML: (element) => element.getAttribute('data-border-radius'),
        renderHTML: (attributes) => ({
          'data-border-radius': attributes.borderRadius,
        }),
      },
      buttonColor: {
        default: '#000000',
        parseHTML: (element) => element.getAttribute('data-button-color'),
        renderHTML: (attributes) => ({
          'data-button-color': attributes.buttonColor,
        }),
      },
      textColor: {
        default: '#ffffff',
        parseHTML: (element) => element.getAttribute('data-text-color'),
        renderHTML: (attributes) => ({
          'data-text-color': attributes.textColor,
        }),
      },
    }
  },

  addCommands() {
    return {
      setButton:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
      setButtonAttributes:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div[data-email-component="${this.name}"]`,
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const {
      text,
      url: href,
      alignment,
      variant,
      borderRadius,
      buttonColor,
      textColor,
    }: Partial<ButtonAttributes> = node.attrs
    const style = generateButtonStyles({
      alignment,
      variant,
      borderRadius,
      buttonColor,
      textColor,
    })
    HTMLAttributes.style = [
      'display: flex',
      'position: relative',
      'max-width: 100%',
      'width: 100%',
      'max-height: 100%',
      'border-radius: 3px',
    ].join(';')
    const element = document.createElement('a')
    element.href = href || ''
    element.target = '_blank'
    element?.setAttribute('style', style)
    element.innerText = text || ''
    return [
      'div',
      mergeAttributes(
        {
          'data-email-component': this.name,
        },
        HTMLAttributes,
      ),
      element,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(EditorButtonComponent)
  },
})
